import { ActivatedRoute, Router } from '@angular/router';
import { Component, EventEmitter, HostListener, Inject, Input, LOCALE_ID, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { switchMap } from 'rxjs/operators';

import { AutoSub, AutoSubOnDestroy, MediaService } from '../../providers';
import { AllowedMediaMap, ContainerOptions, ContainerType, DatabaseModel, dataUrlToFile, Language, Media, MediaFile, MotesPaths } from '../../models';
import { UploadMediaService } from '../../components/upload-media/upload-media.service';
import { UploadCropperComponent } from '../../components/upload-cropper/upload-cropper.component';

import { BlogPostService } from './blog-post.service';
import { BlogPost } from './blog-post';
import { MediumEditorComponent } from './medium-editor.component';
import { EditorTabId } from '../cms-editor/cms-editor.component';
import { PageMetaService } from '../../providers/page-meta.service';

@AutoSubOnDestroy()
@Component({
    selector: 'app-blog-post',
    templateUrl: './blog-editor.component.html',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['../../../../node_modules/medium-editor/dist/css/medium-editor.min.css',
        '../../../../node_modules/medium-editor/dist/css/themes/default.css',
        './blog-editor.component.scss']
})
export class BlogEditorComponent implements OnInit {
    public postKey: string;
    @Input('postKey') set setPostKey(postKey: string) {
        this.postKey = postKey;
        this.initPostWatch(postKey)
    }

    @Input() editMode = false;
    @Output() editModeChange = new EventEmitter<boolean>();

    @Input() showSave = true;
    @Input() showAdd = true;
    @Input() showDelete = true;

    @Output() saveEntry = new EventEmitter<void>();
    @Output() addEntry = new EventEmitter<void>();
    @Output() deleteEntry = new EventEmitter<void>();

    @ViewChild('mediumEditor') editor: MediumEditorComponent;

    public uploadCropper: UploadCropperComponent;
    public post: BlogPost = null;
    // Sanitized version of blog post content
    public safeContent: SafeHtml = '';
    public articleSaved: boolean = false;

    // Media
    public newMedia: Media = null;
    public mediaList: Media[] = [];
    public imageDiv: HTMLImageElement = null;
    public activeEditorTab: EditorTabId = EditorTabId.tags;
    private postKeyWasChanged = false;

    private readonly defaultPost = {
        key: '',
        type: 'POST',
        description: "A blog post about StoryHunt",
        title: "",
        user: "mathias",
        userName: "Mathias Mølgaard",
        createdOn: Date.now(),
        keywords: "Experiences, Great Company, Stories",
        language: this.locale,
        content: `<h2>Subheader</h2>Suspendisse ac nulla ut massa consequat rutrum. Sed ex diam, aliquam vitae lacus eu, fringilla bibendum lacus. Orci varius natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Sed pellentesque pellentesque iaculis. Morbi venenatis lorem leo, aliquam vehicula erat lobortis vel. Nulla facilisi. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Maecenas viverra mi id ante bibendum, non tincidunt magna scelerisque. Nulla dignissim neque a risus maximus finibus. Vestibulum quis blandit purus. Vivamus accumsan orci eu odio rutrum, id sagittis justo pulvinar. Suspendisse interdum tellus at ante volutpat posuere. <br><br>Vestibulum dignissim nec ipsum at vestibulum. Nulla finibus libero a nunc rhoncus tristique. Donec quis magna vitae risus cursus porta quis et justo. Nulla at urna nec erat mattis lacinia eget ac turpis. Quisque ac imperdiet dui, a sagittis enim. Nulla leo dolor, molestie id dapibus vel, molestie ut ipsum. Pellentesque habitant morbi tristique senectus et netus et malesuada fames ac turpis egestas.`
    };

    private readonly nonExistingPost = {
        key: '',
        type: 'CUSTOM',
        description: 'Page description...',
        title: 'Page title...',
        user: "",
        userName: "",
        createdOn: Date.now(),
        language: this.locale,
        keywords: 'Page keywords...',
        content: ''
    };

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private blogPostService: BlogPostService,
        private uploadMediaService: UploadMediaService,
        private sanitizer: DomSanitizer,
        private pageMetaService: PageMetaService,
        @Inject(LOCALE_ID) public locale: string) {
    }

    ngOnInit() {
        this.initPostWatch(this.postKey);
    }

    private initPostWatch(postKey: string) {
        let watchPost = (key: string) => this.blogPostService.watchPostSsr(this.route.snapshot, key);

        // If we dident get the post key from input, try to get it from paramMap
        let watchActivePost = postKey ? watchPost(postKey) : this.route.paramMap.pipe(switchMap(param => watchPost(param.get("postKey"))));

        AutoSub(this).reg['postSub'] = watchActivePost.subscribe((post) => {
            // Allow editing of non existing posts
            if(!post || post.key == 'new') {
                // Return post does not exists
                if(!post) return this.post = new BlogPost(this.nonExistingPost);
                // Return new blog post with default values
                return this.post = new BlogPost(this.defaultPost)
            };

            // Update post
            this.post = post;
            // Set page meta data for SEO on all pages using BlogPost as model
            let postImage = this.post.image?.hd?.fallback;
            this.pageMetaService.setPageMeta(this.post, postImage);

            // Sanitize html and show it in div (this is needed to overcome xss warning and fix WYSIWYG editing)
            this.safeContent = this.sanitizer.bypassSecurityTrustHtml(this.post.content);
            // Init media list with all media from post
            this.mediaList = DatabaseModel.mapToArray(this.post.mediaMap);
        });
    }

    public saveContent() {
        this.articleSaved = true;
        // Make sure content from HTML editor updated
        this.editor.updateContent();
        // Get content from editor
        this.post.content = this.editor.mediumEditor.getContent();
        // Set post key from origin
        this.post.key = this.generatePostKey();
        // Sanitize html and show it in div (this is needed to overcome xss warning and fix WYSIWYG editing)
        this.safeContent = this.sanitizer.bypassSecurityTrustHtml(this.post.content);

        this.blogPostService.savePost(this.post);
        this.editMode = false;
        this.saveEntry.emit();
    }

    public deleteBlog() {
        let ok = confirm("Really, delete this page? (You can't go back)");
        if(!ok) return;

        if(this.post) {
            this.blogPostService.deletePost(this.post.key);
        }
        this.editMode = false;
        this.post.title = this.post.title + " (deleted)";
        this.deleteEntry.emit();
    }

    public addBlog() {
        this.editMode = false;
        // Make a nice open close animation
        setTimeout(() => {
            this.editMode = true;
        }, 300)

        this.router.navigateByUrl('blog/new');
        this.addEntry.emit();
    }

    public setEditMode(editModeEnabled: boolean) {
        // Update preview with unsaved data
        if(this.editor && this.editor.mediumEditor && this.post) this.post.content = this.editor.mediumEditor.getContent();
        this.editMode = editModeEnabled;
        this.editModeChange.emit(editModeEnabled);
    }

    public async imageAdded(imageDiv: HTMLImageElement) {
        let media = new Media({}, imageDiv.id);
        let file = media.original;
        file.url = imageDiv.src;
        file.blob = dataUrlToFile(imageDiv.src, 'origin.png');
        file.storage = MotesPaths.POST_HD_PATH() + media.key + "_origin.png";
        this.mediaList.push(media);

        // Let angular update list of media elements to show upload status - this is very important for the uploadMedia component to initialize all paths
        // - could we do this smarter - let the upload component init the patch etc..? probably
        this.activeEditorTab = EditorTabId.media;
        await new Promise<void>((resolve) => setTimeout(() => { resolve() }, 0));
        // Upload image
        const allowedMediaTypes: AllowedMediaMap = { IMAGE: true, VIDEO: true, SOUND: true };
        const containerOptions: ContainerOptions = { type: ContainerType.BLOG_POST, containerKey: this.postKey};
        this.uploadMediaService.uploadMedia(media, file, this.uploadCropper, UploadMediaService.DefaultImageFormat, allowedMediaTypes, containerOptions);
    }

    public onFrontImageUploadDone(media: Media) {
        if(!media.hd.url) {
            console.log("Blog front media was deleted... do nothing ", media);
            return;
        }
        this.post.image = media;
    }

    public onContentImageUploadDone(media: Media) {
        let imageDiv = <HTMLImageElement>document.getElementById(media.key);
        if(!media.hd.url) {
            console.log("Media was deleted ", media);
            // Remove image from dom if it exists
            if(imageDiv) imageDiv.remove();
            // Remove media from list
            delete this.post.mediaMap[media.key];
            this.mediaList = DatabaseModel.mapToArray(this.post.mediaMap);
            return;
        }

        if(imageDiv) {
            // console.log("Set image src of image in editor url: " + media.hd.url, imageDiv, media);
            imageDiv.src = media.hd.url;
        }
        this.saveMedia(media);
    }

    public saveMedia(media: Media) {
        if(!media.project) media.project = 'storyhunt-home';
        // this.mediaService.updateMedia(media);
        if(!this.post) return console.warn("Can't add media - no post?", media);

        if(!this.post.mediaMap) this.post.mediaMap = {};
        // Save media in blog post
        this.post.mediaMap[media.key] = media;
        this.mediaList = DatabaseModel.mapToArray(this.post.mediaMap);
    }

    // Install hostlistener to handle routerLinks in document
    @HostListener('document:click', ['$event'])
    public handleClick(event: Event): void {
        // If this is not a router link, immediately return
        if(!(event.target instanceof HTMLAnchorElement)) return;
        if(event.target.className != 'routerLink') return;

        event.preventDefault();
        const element = event.target as HTMLAnchorElement;
        const route = element ? element.getAttribute('href') : "";

        if(route) {
            this.router.navigateByUrl(`/${route}`);
        }
    }

    private generatePostKey(): string {
        // Append post language or locale to origin key if it is not already there
        let langExtension = this.post.language || this.locale;
        return `${this.post.originKey}_${langExtension}`;
    }
}
